import { useField } from 'formik';
import { Space } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { useMultiStepFormContext } from 'components/new-ui/composed/multistep-form/MultiStepFormProvider';
import FormSectionHeader from 'components/new-ui/base/form/FormSectionHeader';
import FormAutocomplete from 'components/new-ui/composed/formAutocomplete/FormAutocomplete';
import ReadonlyFormItem from 'components/new-ui/base/form/ReadonlyFormItem';
import { CreateJobFormFields } from 'views/jobs/validationSchemas';
import { CreateJobFormSteps } from 'views/jobs/types';
import { useEffect, useMemo, useState } from 'react';
import { Team } from 'store/teams/types';
import { getTeamById } from 'store/teams/client';
import Notification from 'components/ui/base/notification/Notification';
import { DefaultOptionType } from 'antd/lib/select';
import { IndividualType } from 'store/individuals/individual';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import useManifests from 'hooks/useManifests';

const fieldName = 'assignAgent.assignedAgent';

const AssignAgentSection = () => {
    const { manifest } = useManifests();
    const [_field, _meta, helpers] = useField(fieldName);
    const [teamField] = useField('team.teamId');

    const { currentStepConfig, isIdle, isComplete, isActive, resetStep, stepsConfig, lastResetEvent } =
        useMultiStepFormContext<CreateJobFormFields>();
    const { hideGlobalOverlay, showGlobalOverlay } = useGlobalOverlay();

    const [team, setTeam] = useState<Team>();
    const [teamMemberInput, setTeamMemberInput] = useState('');

    const stepKey = CreateJobFormSteps.ASSIGN_AGENT;
    const disabled = currentStepConfig?.disabled || false;
    const sectionTitle = 'Assign Agent (Optional)';
    const isActiveStep = isActive(stepKey);

    const currentStepIndex = useMemo(() => stepsConfig.findIndex((s) => s.key === stepKey), [stepKey, stepsConfig]);

    const teamMembersOptions: DefaultOptionType[] = useMemo(() => {
        return (
            team?.members
                .filter((m) => m.role === IndividualType.CLEARANCE_AGENT)
                .map((m) => ({
                    value: m.individual.id,
                    label: m.individual.fullName,
                })) ?? []
        );
    }, [team?.members]);

    const filteredTeamMembers: DefaultOptionType[] = useMemo(() => {
        const q = teamMemberInput.trim().toLowerCase();
        if (!q) return teamMembersOptions;
        return teamMembersOptions.filter((o) => String(o.label).toLowerCase().includes(q));
    }, [teamMembersOptions, teamMemberInput]);

    const handleSearchTeamMembers = (val: string) => {
        setTeamMemberInput(val);
    };

    const handleSelectTeamMember = (val: string, option?: DefaultOptionType) => {
        helpers.setValue(val);
        setTeamMemberInput(String(option?.label ?? ''));
    };

    const handleCreateTeamMember = () => {
        setTeamMemberInput('');
        helpers.setValue(undefined);
    };

    const handleReset = () => {
        resetStep(CreateJobFormSteps.ASSIGN_AGENT, 'assignedAgent');
        setTeamMemberInput('');
    };

    useEffect(() => {
        if (!isActiveStep) {
            return;
        }

        const fetchTeam = async () => {
            try {
                showGlobalOverlay({
                    type: 'LoadingOverlay',
                    payload: {
                        message: 'Getting team information...',
                    },
                });

                if (!manifest?.team?.id) {
                    throw new Error(
                        'Could not get team details for this manifest. Please refresh the page and try again'
                    );
                }

                const team = await getTeamById(manifest?.team.id);

                if (!team.id) {
                    throw new Error('Could not get team details. Please refresh the page and try again');
                }

                setTeam(team);
            } catch (err) {
                const errorDetails = err instanceof Error && err.message;
                Notification({
                    messageTitle: 'Could not get team details',
                    description: errorDetails || 'Something went wrong. Please refresh the page and try again',
                    type: 'error',
                    placement: 'top',
                });
            } finally {
                hideGlobalOverlay();
            }
        };

        fetchTeam();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teamField.value, isActiveStep]);

    // Reset the field if some of the previous form sections have been reset
    useEffect(() => {
        if (lastResetEvent?.stepIndex == null) return;

        if (lastResetEvent.stepIndex > -1 && lastResetEvent.stepIndex < currentStepIndex) {
            setTeamMemberInput('');
            helpers.setValue(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResetEvent, currentStepIndex]);

    if (isIdle(stepKey)) {
        return <FormSectionHeader title="Assign Agent (Optional)" icon={<CheckCircleOutlined />} />;
    }

    if (isActive(stepKey)) {
        return (
            <Space style={{ width: '100%' }} direction="vertical" size={16}>
                <FormSectionHeader
                    icon={<CheckCircleOutlined />}
                    title={sectionTitle}
                    subtitle="Assign a Clearance Agent to complete the clearances for this Job."
                />
                <FormAutocomplete
                    id="clearanceAgent"
                    style={{ width: '100%' }}
                    inputPlaceholder="Search Agent"
                    fieldName={fieldName}
                    options={filteredTeamMembers}
                    value={teamMemberInput}
                    onSearch={handleSearchTeamMembers}
                    onSelect={handleSelectTeamMember}
                    onClear={handleCreateTeamMember}
                    onBlur={() => helpers.setTouched(true)}
                />
            </Space>
        );
    }

    if (isComplete(stepKey)) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                }}
            >
                <FormSectionHeader title={sectionTitle} icon={<CheckCircleOutlined />} />
                <ReadonlyFormItem disabled={disabled} onReset={handleReset} value={teamMemberInput} />
            </div>
        );
    }

    return null;
};

export default AssignAgentSection;
